/**
 * Everything chat needs once per signed-in tab (ADR 0015): the live stream, the app-wide
 * shortcuts (Ctrl+K / ⌘K search and commands, Ctrl+Shift+M dock, Alt+↑/↓ next unread chat), the unread count in
 * the tab title "(3) …" and a favicon badge, desktop notifications + sound while the tab is open
 * but not in front (when this device gets no Web Push), automatic ไม่อยู่ after 10 idle minutes,
 * notification-click routing from the service worker, the call layer and the docked chat.
 */
import { Suspense, useEffect, useRef, useState } from 'react';
import { useSession } from '../app/session.js';
import { importWithReload, lazyPage, whenIdle } from '../lib/lazy.js';
import { navigate, parseHash } from '../lib/router.js';
import { effectiveTheme, setPrefs } from '../lib/preferences.js';
import { chatApi, pushApi } from './api.js';
import { ChatDock, setDockOpen } from './ChatDock.js';
import { chatStore, isMuted, previewOf, useChat } from './store.js';

// Voice / video calls (BETA): their code arrives with this tab's first call event, or when the chat page
// starts a call — not with every first visit.
const CallLayer = lazyPage(() => import('./calls.js'), 'CallLayer');

// Ctrl+K / ⌘K palette (palette/CommandPalette.tsx): downloaded the first time it is opened, or warmed
// when the pointer reaches the header's search button. Only this listener and the button are in the shell.
const CommandPalette = lazyPage(() => import('../palette/CommandPalette.js'), 'CommandPalette');

let openSwitcher: (() => void) | null = null;
/** Open the Ctrl+K palette ("ไปที่…": pages, commands, work, files, letters, chats, people) from anywhere, e.g. the header button. */
export function openQuickSwitcher() {
  openSwitcher?.();
}
const THEME = { dark: () => effectiveTheme() === 'dark', set: (theme: 'light' | 'dark') => setPrefs({ theme }) };
/** Start downloading the palette (pointer over / focus on a search button). */
export const preloadQuickSwitcher = () => CommandPalette.preload();

/**
 * Where Ctrl+K keeps its own meaning: rich-text editing (contenteditable — "insert link" in most
 * editors) and any element that declares its own keys (`data-own-keys`). Plain inputs and textareas
 * do not use Ctrl+K in this app (the design editor's text box stops its keys before they reach here),
 * so the palette opens from them too.
 */
function ownsCtrlK(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('[data-own-keys]')) return true;
  return target instanceof HTMLElement && target.isContentEditable;
}
import type { Message } from './types.js';

const IDLE_MS = 10 * 60_000;
const BASE_ICON = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='8' fill='%234f46d5'/%3E%3Cpath d='M9 9l14 14M23 9L9 23' stroke='white' stroke-width='3.5' stroke-linecap='round'/%3E%3C/svg%3E";

/** "(3) title" — kept in place even when a page sets its own title. */
function applyTitle(n: number) {
  const bare = document.title.replace(/^\(\d+\+?\)\s/, '');
  const next = n > 0 ? `(${n > 99 ? '99+' : n}) ${bare}` : bare;
  if (document.title !== next) document.title = next;
}

let faviconN = -1;
function applyFavicon(n: number) {
  if (n === faviconN) return;
  faviconN = n;
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return;
  if (n <= 0) {
    link.href = BASE_ICON;
    return;
  }
  const img = new Image();
  img.onload = () => {
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const g = c.getContext('2d');
    if (!g) return;
    g.drawImage(img, 0, 0, 64, 64);
    g.fillStyle = '#d92d20';
    g.beginPath();
    g.arc(46, 18, 18, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#fff';
    g.font = 'bold 24px system-ui, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(n > 9 ? '9+' : String(n), 46, 19);
    link.href = c.toDataURL('image/png');
  };
  img.src = BASE_ICON;
}

function beep() {
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = 740;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.32);
    setTimeout(() => void ctx.close().catch(() => undefined), 500);
  } catch {
    /* no audio */
  }
}

export function ChatHost() {
  const session = useSession();
  const store = useChat();
  const [switcher, setSwitcher] = useState(false);
  const [calls, setCalls] = useState(false);
  const sound = useRef(true);
  const pushOn = useRef(false);
  const userId = session.user?.id ?? null;
  const workspaceId = session.workspace?.id ?? null;

  // Live stream follows the session and the selected workspace.
  useEffect(() => {
    if (session.status === 'authenticated' && session.user && workspaceId && !session.mustChangePassword) chatStore.start(session.user, workspaceId);
    else if (session.status === 'anonymous') chatStore.stop();
  }, [session.status, session.user, workspaceId, session.mustChangePassword]);

  // The call layer loads on the first call event; events that arrive meanwhile are handed to it in order.
  // It is also warmed once the tab is idle, so a ring finds this release's code at hand.
  useEffect(() => (userId ? whenIdle(CallLayer.preload, 20_000) : undefined), [userId]);
  useEffect(() => {
    if (!userId || calls) return;
    let pending: [string, unknown][] | null = null;
    const off = chatStore.onLive((event, d) => {
      if (!event.startsWith('call.')) return;
      if (pending) {
        pending.push([event, d]);
        return;
      }
      pending = [[event, d]];
      void importWithReload(() => import('./calls.js')).then((m) => {
        off();
        m.callController.bind();
        for (const [e, data] of pending!) m.callController.handle(e, data);
        setCalls(true);
      }, () => {
        pending = null; // a later event tries again
      });
    });
    // A call started from this tab (calls.tsx CALLS_NEEDED): the module is loaded already.
    const needed = () => setCalls(true);
    window.addEventListener('xc-calls-needed', needed);
    return () => {
      off();
      window.removeEventListener('xc-calls-needed', needed);
    };
  }, [userId, calls]);

  // Settings used by in-page alerts (sound) and whether this device already gets pushes.
  useEffect(() => {
    if (!userId) return;
    void pushApi.settings().then((s) => (sound.current = s.sound)).catch(() => undefined);
    void navigator.serviceWorker?.getRegistration?.().then((r) => r?.pushManager?.getSubscription()).then((sub) => (pushOn.current = !!sub)).catch(() => undefined);
    const onSettings = (e: Event) => {
      const d = (e as CustomEvent<{ sound?: boolean; push?: boolean }>).detail;
      if (d.sound !== undefined) sound.current = d.sound;
      if (d.push !== undefined) pushOn.current = d.push;
    };
    window.addEventListener('xc-chat-settings', onSettings);
    return () => window.removeEventListener('xc-chat-settings', onSettings);
  }, [userId]);

  // Tab title + favicon badge.
  const total = store.unreadChats + store.activityUnread;
  useEffect(() => {
    applyTitle(total);
    applyFavicon(total);
    const title = document.querySelector('title');
    if (!title) return;
    const mo = new MutationObserver(() => applyTitle(chatStore.unreadChats + chatStore.activityUnread));
    mo.observe(title, { childList: true, characterData: true, subtree: true });
    return () => mo.disconnect();
  }, [total]);

  // New messages while the tab is not in front: sound, and a desktop notification unless Web Push covers this device.
  useEffect(() => chatStore.onLive((event, d) => {
    if (event !== 'message' && event !== 'notification') return;
    const hidden = document.visibilityState !== 'visible' || !document.hasFocus();
    if (event === 'message') {
      const m = d.message as Message;
      if (m.authorId === chatStore.me || m.kind === 'system' || m.workspaceId !== chatStore.workspaceId) return;
      const c = chatStore.conversations.get(m.conversationId);
      if (!c || isMuted(c)) return;
      const viewing = chatStore.focused === m.conversationId && !hidden;
      if (viewing) return;
      if (sound.current) beep();
      if (hidden && !pushOn.current && 'Notification' in window && Notification.permission === 'granted') {
        void showLocal(`${m.authorName ?? 'ข้อความใหม่'}${c.kind === 'dm' ? '' : ` · ${c.title}`}`, previewOf(m), `/chat/${m.conversationId}?m=${m.id}`, `c${m.conversationId}`);
      }
    }
  }), []);

  // Idle → ไม่อยู่ (and back).
  useEffect(() => {
    if (!userId) return;
    let idle = false;
    let timer: ReturnType<typeof setTimeout>;
    const set = (v: boolean) => {
      if (v === idle) return;
      idle = v;
      void chatApi.status({ idle: v }).catch(() => undefined);
    };
    const active = () => {
      set(false);
      clearTimeout(timer);
      timer = setTimeout(() => set(true), IDLE_MS);
    };
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'visibilitychange'] as const;
    for (const e of events) window.addEventListener(e, active, { passive: true });
    active();
    return () => {
      clearTimeout(timer);
      for (const e of events) window.removeEventListener(e, active);
    };
  }, [userId]);

  // The header's "ค้นหา" button opens the same Ctrl+K palette (ADR 0052).
  useEffect(() => {
    openSwitcher = () => setSwitcher(true);
    return () => {
      openSwitcher = null;
    };
  }, []);
  // App-wide shortcuts.
  useEffect(() => {
    if (!userId) return;
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k') {
        if (ownsCtrlK(e.target)) return;
        e.preventDefault();
        setSwitcher(true);
      } else if (mod && e.shiftKey && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        setDockOpen();
      } else if (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        const unread = chatStore.sorted().filter((c) => c.unread > 0);
        if (!unread.length) return;
        e.preventDefault();
        const cur = parseHash(window.location.hash).path.split('/')[2];
        const i = unread.findIndex((c) => c.id === cur);
        const next = i < 0 ? unread[0]! : unread[(i + (e.key === 'ArrowDown' ? 1 : unread.length - 1)) % unread.length]!;
        navigate(`/chat/${next.id}`);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [userId]);

  // Notification clicks (service worker) open the right place in this tab.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { type?: string; url?: string } | null;
      if (d?.type === 'xc-navigate' && typeof d.url === 'string' && d.url.startsWith('/')) navigate(d.url);
    };
    navigator.serviceWorker?.addEventListener('message', onMsg);
    return () => navigator.serviceWorker?.removeEventListener('message', onMsg);
  }, []);

  if (!userId) return null;
  return (
    <>
      <div className="xc-sr-only" aria-live="polite" aria-atomic="true">{store.announcement}</div>
      <ChatDock />
      {calls && <Suspense fallback={null}><CallLayer /></Suspense>}
      {switcher && <Suspense fallback={null}><CommandPalette onClose={() => setSwitcher(false)} theme={THEME} /></Suspense>}
    </>
  );
}

async function showLocal(title: string, body: string, url: string, tag: string) {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      await reg.showNotification(title, { body, tag, data: { url }, icon: '/icons/icon-192.png', badge: '/icons/badge-72.png' });
      return;
    }
    const n = new Notification(title, { body, tag, icon: '/icons/icon-192.png' });
    n.onclick = () => {
      window.focus();
      navigate(url);
      n.close();
    };
  } catch {
    /* notifications blocked */
  }
}
